import { useCallback, useEffect, useRef, useState } from 'react';
import { maakWachtrij } from '../lib/wachtrij';
import {
  base64NaarBytes,
  pcmNaarAudioBuffer,
  pcmNaarWav,
  speelBestand,
  speelBuffer,
} from '../lib/audio';
import { contextStand, kort, meten, noteer } from '../lib/meetlog';

/**
 * Hoe lang de klok van de AudioContext stil mag staan na het starten van een
 * fragment voordat we de context dood verklaren.
 *
 * Gemeten op 11 september 2026: na het loslaten van de microfoon begint het
 * eerste geluid ongeveer 300 ms later. Een seconde is daar ruim boven en nog
 * altijd korter dan het eerste woord van een vertaling.
 */
const WAAKHOND_MS = 1000;

/**
 * Hoeveel langer dan het fragment zelf we op 'ended' wachten voordat we de
 * speler vrijgeven. Dezelfde marge als de wekker in speelBuffer.
 */
const EINDE_MARGE_MS = 5000;

/** Hoe vaak het meetlog de klok van de context noteert. */
const HARTSLAG_MS = 30_000;

/**
 * Houd de context wakker: een stille lus die blijft draaien.
 *
 * DE OORZAAK VAN DE STILTE OP DE IPHONE, GEMETEN OP 12 SEPTEMBER 2026 met het
 * meetlog in het gesprek. De kamer ging open op 19 s; het eerste bericht kwam
 * op 182 s. De klok van de context stond toen op 95,7 en had op 170 moeten
 * staan: hij was rond 107 s bevroren, terwijl er NIETS gebeurde - geen
 * opname, geen tik. iOS zet een geluidskanaal dat een tijd niets uitstuurt
 * stilletjes uit, en de context blijft 'running' zeggen. Dat past bij alles:
 * de stilte van 's ochtends (wachten op de Mac), het werken na opnieuw
 * binnenkomen (bericht kwam snel), de meetpagina die het nooit zag (stappen
 * volgen elkaar snel op), en de 45 seconden stilte van sessie 10.
 *
 * Dus: zolang de kamer open is, stuurt de context altijd iets uit - een
 * seconde stilte, in een lus. Hoort niemand, en iOS houdt het kanaal open.
 * De bekende manier; de eerste versie hiervan (een sample, een keer) was de
 * opwarmer van dezelfde ochtend, en die bleek niet genoeg.
 *
 * Hoogstens EEN lus per context. Wordt aangeroepen op elke plek waar we weten
 * dat de context loopt: bij het ontgrendelen, bij een geslaagde aanraking en
 * vlak voor het afspelen. Een context die dichtgaat neemt zijn lus mee.
 */
/** De wakkerhoudtoon: 30 Hz, ver onder wat een telefoonspeaker kan. */
const WAKKER_HZ = 30;
/** Ongeveer -50 dB. Hoort niemand, en het zijn geen nullen. */
const WAKKER_STERKTE = 0.003;

const wakkerLussen = new WeakMap<AudioContext, AudioBufferSourceNode>();

function warmOp(ctx: AudioContext): void {
  if (wakkerLussen.has(ctx)) return;
  try {
    // NIET STIL MAAR ONHOORBAAR. Gemeten op 12 september 2026, tweede ronde:
    // met een lus van nullen ging het kanaal acht seconden na het laatste
    // echte geluid alsnog naar 'interrupted'. iOS kijkt dus naar wat er
    // uitkomt, niet of er iets loopt. Daarom een toon van 30 Hz op -50 dB:
    // een telefoonspeaker kan die niet weergeven, en het zijn geen nullen.
    const lus = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const kanaal = lus.getChannelData(0);
    for (let i = 0; i < kanaal.length; i++) {
      kanaal[i] = Math.sin((2 * Math.PI * WAKKER_HZ * i) / ctx.sampleRate) * WAKKER_STERKTE;
    }
    const bron = ctx.createBufferSource();
    bron.buffer = lus;
    bron.loop = true;
    bron.connect(ctx.destination);
    bron.start(0);
    wakkerLussen.set(ctx, bron);
    noteer(`context wakker gehouden: onhoorbare lus gestart, ${contextStand(ctx)}`);
  } catch {
    // Een context die dit weigert, weigert straks ook het echte fragment; dat
    // pad heeft zijn eigen melding.
  }
}

/**
 * Audio afspelen.
 *
 * DE VALKUIL, EN IK BEN ER ZELF INGETRAPT. Een browser weigert geluid zolang
 * de gebruiker niets heeft aangeraakt. Een AudioContext blijft dan in de stand
 * 'suspended' - stil, zonder foutmelding.
 *
 * De eerste versie riep ontgrendel() aan vanuit een useEffect. Dat werkt niet:
 * een effect draait NA de klik, in een eigen taak, en telt daarom niet als
 * aanraking. Bovendien liep hij bij elke render opnieuw.
 *
 * Nu: een luisteraar op het document zelf, die bij de EERSTE aanraking
 * ontgrendelt en zichzelf daarna weghaalt. Dat is de enige plek waar je zeker
 * weet dat het een echte gebruikersactie is.
 *
 * EN ER IS EEN TERUGVAL, want het gaat alsnog mis. Op 4 september 2026 bleek op
 * een echte Mac dat de opname van de spreker WEL klonk en de vertaling NIET -
 * hetzelfde apparaat, hetzelfde tabblad. Het verschil: de opname gaat door een
 * <audio>-element en de vertaling door de Web Audio API. Dat element heeft veel
 * soepelere regels voor automatisch afspelen.
 *
 * Dus: lukt de AudioContext niet, dan wordt dezelfde PCM in een WAV-jasje
 * gestopt en alsnog via een <audio>-element afgespeeld. Twee wegen naar
 * hetzelfde geluid, en de tweede werkt waar de eerste weigert.
 */

export interface Speler {
  ontgrendel: () => void;
  speel: (base64: string, sampleRate: number, berichtId: string) => Promise<void>;
  /** Meld dat de volgende aanroep een vervolgstuk is van dezelfde zin. */
  meldVervolg: () => void;
  /** De opname van de spreker zelf: een compleet bestand, geen rauwe PCM. */
  speelOpname: (base64: string, mimeType: string, berichtId: string) => Promise<void>;
  stop: () => void;
  /** Het bericht dat nu klinkt, of null. */
  klinkt: string | null;
  /**
   * true zodra er ERGENS geluid speelt.
   *
   * Nodig omdat `klinkt` maar over EEN bericht gaat. Alle andere knoppen
   * moeten ook uit: twee stemmen door elkaar is onverstaanbaar, en de
   * originele opname liep bovendien langs deze speler heen.
   */
  speeltAf: boolean;
  /** false zolang de browser nog geen geluid toestaat. */
  gereed: boolean;
  fout: string | null;
}

export function useAudioPlayer(): Speler {
  const ctxRef = useRef<AudioContext | null>(null);
  const bronRef = useRef<AudioBufferSourceNode | null>(null);
  /** Welk bericht op DIT moment klinkt. Zie het slot in speel(). */
  const bezigRef = useRef<string | null>(null);
  /** true als de volgende aanroep een vervolgstuk is en het slot mag passeren. */
  const vervolgRef = useRef(false);
  /**
   * Wanneer het lopende fragment afgelopen is, in de klok van de AudioContext.
   *
   * Nodig om stukken NAADLOOS aan elkaar te plakken. Een volgend stuk dat je
   * gewoon start, begint meteen en valt over het vorige heen; met deze tijd
   * begint hij precies waar het vorige ophoudt.
   */
  const eindeRef = useRef(0);
  /**
   * De rij voor alles wat NIET op de klok gepland kan worden.
   *
   * De terugvalweg gebruikt een <audio>-element, en daar bestaat geen klok
   * voor: je kunt een element niet vertellen dat het over vier seconden moet
   * beginnen. Dus wacht het op zijn beurt. Zonder deze rij begon elk stuk
   * meteen en klonken vier stukken door elkaar heen.
   */
  const rijRef = useRef(maakWachtrij());
  /**
   * Welke weg het EERSTE stuk van een bericht nam.
   *
   * De helft op de klok en de helft in de rij is de slechtste van twee: die
   * twee weten niets van elkaar en lopen alsnog over elkaar heen. Dus houdt
   * een bericht de weg aan waarmee het begon.
   */
  const wegRef = useRef<{ id: string; terugval: boolean } | null>(null);
  /**
   * Waarmee de TERUGVALWEG gestopt wordt.
   *
   * stop() kon tot 15 september 2026 alleen de Web Audio-bron stilzetten. Het
   * <audio>-element van de terugvalweg leefde binnen speelBuffer en was van
   * buiten niet te bereiken - dus bleef het doorspelen. Juist wie die weg
   * neemt (een browser waar de AudioContext niet wil) had aan een stopknop
   * dan niets.
   */
  const afbrekenRef = useRef<AbortController | null>(null);

  /**
   * Het fragment dat NIET klonk omdat het geluid op slot zat.
   *
   * Zonder dit was de oranje balk een dood spoor: je tikte erop, het geluid
   * ging aan, en het bericht waar het om begonnen was bleef stil - je moest
   * het met de hand aanklikken. Gemeld door Giovanni op 11 september 2026.
   *
   * Er staat er maar EEN in, met opzet. Een rij opsparen betekent dat je na
   * een tik ineens vier oude zinnen achter elkaar hoort; in een gesprek wil je
   * de laatste, niet het archief.
   */
  const wachtendRef = useRef<{ base64: string; sampleRate: number; berichtId: string } | null>(
    null,
  );

  /**
   * Een verwijzing naar speel(), omdat het ontgrendelen ERBOVEN staat.
   *
   * De haak leest van boven naar beneden en de luisteraar wordt eerder gemaakt
   * dan de functie die hij moet aanroepen. Een ref lost dat op zonder de hele
   * haak te herschikken.
   */
  const speelRef = useRef<((b: string, s: number, id: string) => Promise<void>) | null>(null);

  /** Speelt het wachtende fragment alsnog, na een geslaagde ontgrendeling. */
  const hervatWachtend = useCallback(() => {
    const wachtend = wachtendRef.current;
    if (wachtend === null) return;
    wachtendRef.current = null;
    // Het slot tegen dubbel afspelen staat nog op dit bericht. Zonder deze
    // regel keert speel() meteen terug en gebeurt er weer niets.
    bezigRef.current = null;
    wegRef.current = null;
    void speelRef.current?.(wachtend.base64, wachtend.sampleRate, wachtend.berichtId);
  }, []);
  const [klinkt, setKlinkt] = useState<string | null>(null);
  const [gereed, setGereed] = useState(false);
  /**
   * WAT ER MIS IS, ALS CODE EN NIET ALS ZIN.
   *
   * Hier stond een Nederlandse zin, hardgecodeerd. Die kwam ongewijzigd op het
   * scherm van een Poolse gast terecht, terwijl de vertaling ervan al in alle
   * zestien talen klaarlag. Een hook hoort geen taal te kiezen; het scherm doet
   * dat.
   *
   * EEN code voor alle gevallen, en dat is met opzet. Of het geluid nu vastzit,
   * een fragment stuk is of de browser helemaal niets kan: wat de gebruiker
   * moet DOEN is telkens hetzelfde, namelijk een keer tikken. Drie meldingen
   * die tot dezelfde handeling leiden, zijn drie vertaalrondes voor niets.
   */
  const [fout, setFout] = useState<'geluid_uit' | null>(null);

  const maakContext = useCallback((): AudioContext | null => {
    // EEN GESLOTEN CONTEXT TELT ALS GEEN CONTEXT.
    //
    // Alleen op null toetsen was niet genoeg. De opruiming sloot de context
    // maar liet de verwijzing staan, en dan gaf deze functie een DICHTE
    // context terug. Elke resume() daarop geeft "Cannot resume a closed
    // AudioContext" - in ontwikkeling bij elke herlaadbeurt, want StrictMode
    // koppelt alles een keer aan, los, en weer aan.
    //
    // Ook een browser kan een context zelf sluiten, bijvoorbeeld als het
    // geluidsapparaat wisselt. Daarom hier meten en niet aannemen: er wordt
    // gewoon een nieuwe gemaakt.
    if (ctxRef.current !== null && ctxRef.current.state !== 'closed') return ctxRef.current;
    ctxRef.current = null;
    const Ctx =
      window.AudioContext ??
      (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctx === undefined) {
      setFout('geluid_uit');
      return null;
    }
    // 24 kHz: dezelfde frequentie als waarin de server de spraak levert.
    const ctx = new Ctx({ sampleRate: 24000 });
    ctxRef.current = ctx;
    noteer(`context gemaakt: ${contextStand(ctx)} rate=${ctx.sampleRate}`);
    // Elke wisseling van stand in het verslag. Op iOS is 'interrupted' de
    // stand die je nergens anders ziet, en precies die wil je terugvinden.
    ctx.onstatechange = () => {
      noteer(`context wisselt: ${contextStand(ctx)}`);
      // ELKE STAND DIE NIET 'running' IS, PROBEREN TE HERVATTEN. WebKit zet
      // een onderbroken context op 'interrupted' en daarna op 'suspended', en
      // hervat hem niet zelf. Wij wel, zonder te wachten op een tik: na de
      // eerste ontgrendeling mag dat in WebKit. Lukt het niet, dan zegt het
      // log dat, en doet het afspeelpad straks een nieuwe poging. Niet op een
      // context die wij zelf dichtdoen: die is dan al uit ctxRef.
      if (ctx.state !== 'running' && ctxRef.current === ctx) {
        void ctx
          .resume()
          .then(() => noteer(`context zelf hervat: ${contextStand(ctx)}`))
          .catch((fout: unknown) =>
            noteer(`context zelf hervatten MISLUKT: ${fout instanceof Error ? fout.message : '?'}`),
          );
      }
    };
    return ctx;
  }, []);

  const ontgrendel = useCallback(() => {
    const ctx = maakContext();
    if (ctx === null) return;
    noteer(`ontgrendel: vooraf ${contextStand(ctx)}`);
    // Eerst laten klinken, dan hervatten. Zie warmOp: dit is de klik, en een
    // start() binnen de klik telt als gebruikersactie.
    warmOp(ctx);
    // Een .catch() hoort hier ALTIJD bij. Zonder wordt een geweigerde resume
    // een losse fout in de console, en die zegt niets over wat de gebruiker
    // moet doen. Lukt het niet, dan blijft `gereed` gewoon false en staat de
    // ontgrendelknop er nog.
    void ctx
      .resume()
      .then(() => {
        const gelukt = ctx.state === 'running';
        noteer(`ontgrendel: na resume ${contextStand(ctx)}`);
        setGereed(gelukt);
        if (gelukt) hervatWachtend();
        // DE MELDING MOET OOK WEG. Zonder deze regel bleef de oranje balk staan
        // nadat je erop had getikt en het geluid gewoon aan was - tot het
        // volgende fragment speelde. Je tikt, er verandert niets zichtbaars, en
        // dus tik je nog een keer.
        if (gelukt) setFout(null);
      })
      .catch(() => setGereed(false));
  }, [maakContext, hervatWachtend]);

  /**
   * De luisteraar op het document. Elke klik, tik of toetsaanslag telt, waar hij
   * ook valt.
   *
   * HIJ BLIJFT STAAN ZOLANG DE KAMER OPEN IS, EN DAT IS EEN REPARATIE VAN
   * 9 SEPTEMBER 2026. Hiervoor haalde hij zichzelf weg zodra het één keer
   * gelukt was. Dat leek zuinig en was een bug: op iOS zet een tabbladwissel de
   * AudioContext terug op 'suspended', en dan luisterde er niemand meer. De
   * tekst op het scherm beloofde "tik ergens op het scherm", maar alleen de
   * knop werkte nog. Giovanni liep daar op zijn iPhone tegenaan.
   *
   * Blijven staan kost vrijwel niets: draait de context al, dan is de eerste
   * regel hieronder een vergelijking en gebeurt er verder niets.
   */
  useEffect(() => {
    const opAanraking = (): void => {
      const ctx = maakContext();
      // Draait hij al? Dan is er niets te doen. Dit is de hele prijs die we
      // betalen voor een luisteraar die blijft staan.
      if (ctx === null || ctx.state === 'running') return;
      void ctx
        .resume()
        .then(() => {
          if (ctx.state === 'running') {
            warmOp(ctx);
            setGereed(true);
            hervatWachtend();
            // Ook hier de melding weghalen: je hebt zojuist getikt, dus de
            // oranje balk heeft zijn werk gedaan.
            setFout(null);
          }
        })
        .catch(() => {
          // Nog niet toegestaan, of de context ging tussentijds dicht. De
          // luisteraar blijft staan, dus de volgende aanraking probeert het
          // opnieuw. Niets om te melden.
        });
    };

    document.addEventListener('pointerdown', opAanraking);
    document.addEventListener('keydown', opAanraking);
    // Meteen proberen: sommige browsers staan het al toe.
    opAanraking();

    return () => {
      document.removeEventListener('pointerdown', opAanraking);
      document.removeEventListener('keydown', opAanraking);
    };
  }, [maakContext]);

  const stop = useCallback(() => {
    // EERST de terugvalweg, want die speelt buiten de AudioContext om en zou
    // anders gewoon doorgaan terwijl de rij al leeg is.
    afbrekenRef.current?.abort();
    afbrekenRef.current = null;

    if (bronRef.current !== null) {
      try {
        bronRef.current.stop();
      } catch {
        // Al gestopt. Geen probleem.
      }
      bronRef.current = null;
    }
    rijRef.current.leeg();
    bezigRef.current = null;
    eindeRef.current = 0;
    wegRef.current = null;
    setKlinkt(null);
  }, []);

  /**
   * Een compleet audiobestand (webm, ogg, m4a) door hetzelfde slot.
   *
   * Liep hiervoor langs de speler heen, via een los <audio>-element. Daardoor
   * kon de originele stem over de vertaling heen klinken zonder dat iets dat
   * tegenhield.
   */
  const speelOpname = useCallback(
    async (base64: string, mimeType: string, berichtId: string) => {
      if (bezigRef.current !== null) return;
      bezigRef.current = berichtId;
      stop();
      bezigRef.current = berichtId;
      // Door DEZELFDE rij als de terugvalweg. Anders kan de originele stem
      // alsnog over een vertaling heen vallen die nog in de rij staat.
      await rijRef.current.voegToe(async () => {
        setKlinkt(berichtId);
        try {
          afbrekenRef.current = new AbortController();
          await speelBestand(base64, mimeType, afbrekenRef.current.signal);
          setFout(null);
        } catch {
          setFout('geluid_uit');
        }
      });
      if (rijRef.current.open() === 0) {
        bezigRef.current = null;
        setKlinkt(null);
      }
    },
    [stop],
  );

  /**
   * TERUGVAL: als WAV door een <audio>-element. Zie de toelichting bovenaan.
   * VIA DE RIJ, want een element kun je niet vooruit plannen.
   *
   * Los getrokken uit speel() omdat er nu TWEE aanleidingen zijn: een context
   * die niet 'running' wil worden, en een context die 'running' zegt maar
   * waarvan de klok stilstaat (de waakhond hieronder).
   */
  const viaTerugval = useCallback(
    async (bytes: Uint8Array, base64: string, sampleRate: number, berichtId: string) => {
      setGereed(false);
      await rijRef.current.voegToe(async () => {
        try {
          setKlinkt(berichtId);
          noteer(`speel ${kort(berichtId)}: via <audio>`);
          afbrekenRef.current = new AbortController();
          await speelBuffer(pcmNaarWav(bytes, sampleRate), 'audio/wav', afbrekenRef.current.signal);
          noteer(`speel ${kort(berichtId)}: <audio> klaar`);
          setFout(null);
        } catch (fout) {
          noteer(`speel ${kort(berichtId)}: <audio> MISLUKT: ${fout instanceof Error ? fout.message : '?'}`);
          setFout('geluid_uit');
          wachtendRef.current = { base64, sampleRate, berichtId };
        }
      });
      // Alleen opruimen als dit het LAATSTE stuk was. Staat er nog iets in
      // de rij, dan gaat het geluid gewoon door en hoort de melding te
      // blijven staan.
      if (rijRef.current.open() === 0) {
        setKlinkt(null);
        bezigRef.current = null;
      }
    },
    [],
  );

  const speel = useCallback(
    async (base64: string, sampleRate: number, berichtId: string) => {
      // SLOT TEGEN DUBBEL AFSPELEN. Twee keer op de knop, of een bericht dat
      // om wat voor reden dan ook twee keer binnenkomt, gaf twee stemmen door
      // elkaar. De ref en niet de state, want state loopt een render achter en
      // twee klikken vlak na elkaar zitten in dezelfde render.
      //
      // Een VOLGEND STUK van hetzelfde bericht mag er wel doorheen: dat is de
      // rest van dezelfde zin, geen tweede stem.
      if (bezigRef.current === berichtId && !vervolgRef.current) return;
      vervolgRef.current = false;
      bezigRef.current = berichtId;

      const ctx = maakContext();
      if (ctx === null) {
        bezigRef.current = null;
        return;
      }
      noteer(`speel ${kort(berichtId)}: vooraf ${contextStand(ctx)}`);

      const bytes = base64NaarBytes(base64);

      // Een tabblad dat op de achtergrond stond, zet de context op pauze.
      // HIER ZAT HET GAT, EN HET KOSTTE EEN TESTER EEN HEEL GESPREK.
      //
      // Deze regel stond BUITEN elke try. Weigert de browser de resume - en dat
      // doet hij zolang de gebruiker nergens op getikt heeft - dan klapte de
      // functie hier uit, nog voor de plek waar de melding gezet wordt. De
      // aanroeper ving hem op met een lege catch, dus er gebeurde helemaal
      // niets: geen geluid, geen melding, geen spoor.
      //
      // Het verraderlijke is dat de ANDERE weg wel werkte. De originele stem
      // speel je af met een knopdruk, en dat is een echte aanraking, dus daar
      // slaagt de resume. Zo lijkt het geluid te werken terwijl elke vertaling
      // stil verdwijnt.
      // NIET op 'suspended' toetsen maar op 'running'. Dat verschil is op een
      // iPhone het verschil tussen geluid en stilte.
      //
      // WebKit kent een DERDE stand die Chrome en Firefox niet hebben:
      // 'interrupted'. Die ontstaat bij een telefoontje, bij Siri, bij geluid
      // uit een andere app en bij een scherm dat op slot gaat. Hier stond
      // `=== 'suspended'`, en dan werd zo'n context helemaal niet hervat - de
      // functie liep er zonder hervatting langs. Een toets in
      // useAudioPlayer.test.ts houdt dit nu vast.
      if (ctx.state !== 'running') {
        try {
          await ctx.resume();
          noteer(`speel ${kort(berichtId)}: na resume ${contextStand(ctx)}`);
        } catch (fout) {
          noteer(`speel ${kort(berichtId)}: resume GEWEIGERD: ${fout instanceof Error ? fout.message : '?'}`);
          setGereed(false);
          setFout('geluid_uit');
          bezigRef.current = null;
          // Bewaren, zodat de tik op de oranje balk dit alsnog laat klinken.
          wachtendRef.current = { base64, sampleRate, berichtId };
          return;
        }
      }

      // Loopt hij? Dan hoort de stille lus te draaien. Na een verse context
      // (bijvoorbeeld na de waakhond) is dit de eerste plek die hem start.
      if (ctx.state === 'running') warmOp(ctx);

      // Een vervolgstuk houdt de weg van het eerste stuk aan.
      const bekend = wegRef.current;
      const terugval =
        bekend !== null && bekend.id === berichtId ? bekend.terugval : ctx.state !== 'running';
      wegRef.current = { id: berichtId, terugval };

      if (terugval) {
        await viaTerugval(bytes, base64, sampleRate, berichtId);
        return;
      }
      setGereed(true);

      try {
        const buffer = pcmNaarAudioBuffer(bytes, ctx, sampleRate);
        const bron = ctx.createBufferSource();
        bron.buffer = buffer;
        bron.connect(ctx.destination);

        // Aansluiten op wat er nog loopt, of meteen beginnen. Zo klinken de
        // stukken van een lange zin als EEN zin, zonder gat ertussen.
        const nu = ctx.currentTime;
        const beginTijd = eindeRef.current > nu ? eindeRef.current : nu;
        eindeRef.current = beginTijd + buffer.duration;
        const gestartOp = performance.now();

        bron.onended = () => {
          noteer(
            `speel ${kort(berichtId)}: einde na ${Math.round(performance.now() - gestartOp)} ms, ${contextStand(ctx)}`,
          );
          // Alleen opruimen als DIT het laatste geplande fragment was; anders
          // wist een eerder stuk de stand terwijl er nog wat komt.
          if (bronRef.current === bron) {
            bronRef.current = null;
            bezigRef.current = null;
            eindeRef.current = 0;
            setKlinkt(null);
          }
        };
        bron.start(beginTijd);
        bronRef.current = bron;
        setKlinkt(berichtId);
        setFout(null);
        noteer(
          `speel ${kort(berichtId)}: gestart via Web Audio, duur=${buffer.duration.toFixed(2)}s ` +
            `begin=${beginTijd.toFixed(3)} ${contextStand(ctx)}`,
        );

        // DE WAAKHOND. Een context die 'running' zegt maar niet rendert, is op
        // iOS geen theorie: op 12 september 2026 bleef het eerste bericht na
        // het binnenkomen stil, met de spreekknop op slot, en in sessie 10 kwam
        // alles na 45 seconden in een keer los. Een context die niet rendert,
        // laat zijn klok niet lopen. Dus: staat currentTime na een seconde nog
        // op dezelfde stand, dan is deze context dood. Dicht ermee, en dit
        // fragment alsnog via het <audio>-element, dat zijn eigen wekker heeft.
        const klokVooraf = ctx.currentTime;
        window.setTimeout(() => {
          if (bronRef.current !== bron) return;
          if (ctx.currentTime > klokVooraf) return;
          noteer(
            `speel ${kort(berichtId)}: KLOK STAAT STIL na ${WAAKHOND_MS} ms (${contextStand(ctx)}); ` +
              'context dicht, opnieuw via <audio>',
          );
          try {
            bron.onended = null;
            bron.stop();
          } catch {
            // Nooit gestart. Precies het probleem.
          }
          bronRef.current = null;
          eindeRef.current = 0;
          if (ctxRef.current === ctx) ctxRef.current = null;
          void ctx.close().catch(() => {
            // Een dode context die ook niet dicht wil. Laten liggen.
          });
          wegRef.current = { id: berichtId, terugval: true };
          void viaTerugval(bytes, base64, sampleRate, berichtId);
        }, WAAKHOND_MS);

        // DE WEKKER. De klok liep, maar 'ended' bleef uit. Dan is het fragment
        // ergens onderweg weggevallen; de spreekknop mag daar niet voorgoed
        // door op slot blijven. Ruim na de geplande eindtijd vrijgeven.
        const verwachtMs = (beginTijd - nu + buffer.duration) * 1000;
        window.setTimeout(() => {
          if (bronRef.current !== bron) return;
          noteer(
            `speel ${kort(berichtId)}: GEEN einde binnen ${Math.round(verwachtMs + EINDE_MARGE_MS)} ms ` +
              `(${contextStand(ctx)}); vrijgegeven`,
          );
          bronRef.current = null;
          bezigRef.current = null;
          eindeRef.current = 0;
          setKlinkt(null);
        }, verwachtMs + EINDE_MARGE_MS);
      } catch (fout) {
        noteer(`speel ${kort(berichtId)}: Web Audio MISLUKT: ${fout instanceof Error ? fout.message : '?'}`);
        bezigRef.current = null;
        setFout('geluid_uit');
        wachtendRef.current = { base64, sampleRate, berichtId };
      }
    },
    [maakContext, viaTerugval],
  );

  /**
   * Alleen voor het meetlog: elke dertig seconden de stand en de klok. Zo is
   * in een screenshot te zien of de klok doorloopt terwijl er niets gebeurt -
   * precies het moment waarop hij op 12 september bevroor.
   */
  useEffect(() => {
    if (!meten()) return;
    const klok = window.setInterval(() => {
      const ctx = ctxRef.current;
      noteer(`hartslag: ${contextStand(ctx)}`);
      if (ctx === null || ctx.state === 'running') return;
      // Een meting: mag WebKit hier hervatten zonder tik? Het antwoord komt
      // in het log en bepaalt of de oranje balk nog nodig is.
      void ctx
        .resume()
        .then(() => noteer(`hartslag: resume geprobeerd, nu ${contextStand(ctx)}`))
        .catch((fout: unknown) =>
          noteer(`hartslag: resume GEWEIGERD: ${fout instanceof Error ? fout.message : '?'}`),
        );
    }, HARTSLAG_MS);
    return () => window.clearInterval(klok);
  }, []);

  // De verwijzing bijwerken zodra speel() opnieuw gemaakt is. Zie speelRef.
  // IN EEN EFFECT EN NIET TIJDENS HET RENDEREN: React mag een render afbreken
  // en opnieuw doen, en dan heb je een ref gezet voor een render die nooit
  // geteld heeft.
  useEffect(() => {
    speelRef.current = speel;
  }, [speel]);

  useEffect(() => {
    return () => {
      bronRef.current?.disconnect();
      const ctx = ctxRef.current;
      // EERST DE VERWIJZING WEG, DAN PAS SLUITEN. Andersom blijft er een
      // dichte context achter waar de rest van de haak nog op afgaat.
      ctxRef.current = null;
      void ctx?.close().catch(() => {
        // Al gesloten. Dat is precies wat we wilden.
      });
    };
  }, []);

  const meldVervolg = useCallback(() => {
    vervolgRef.current = true;
  }, []);

  return {
    ontgrendel,
    speel,
    speelOpname,
    meldVervolg,
    stop,
    klinkt,
    gereed,
    fout,
    speeltAf: klinkt !== null,
  };
}
